import { ArrowForward } from '@mui/icons-material';
import { useIsMobile } from '@rhg/hooks/use-is-mobile';
import {
    RegistrationFlow,
    RegistrationFlowStep,
} from '@rhg/hooks/use-registration-context/types';
import { useRegistrationDialog } from '@rhg/hooks/use-registration-dialog';
import { useUserBlocker } from '@rhg/hooks/use-user-blocker';
import { UserBlocker } from '@rhg/hooks/use-user-blocker/types';
import commonMessages from '@rhg/lang/messages/common';
import { logger } from '@rhg/libs/logger';
import { Alert, Button, Theme, Typography } from '@rhg/libs/material';
import { UnlockOutline } from '@rhg/libs/material/icons';
import { useIntl } from 'react-intl';
import ClickableWrapper from '../clickable-wrapper';

const styleRoot = {
    borderRadius: 0,
    backgroundColor: (theme: Theme) =>
        theme.palette.mode === 'dark' ? 'info.light' : 'info.dark',
    // This banner inverts its background to a LIGHT color in dark mode and
    // otherwise inherits the filled text color. The MuiAlert dark-mode
    // override makes filled text light, which would render this banner
    // light-on-light. Own the dark-mode text color so it stays as-is
    // (light mode is unaffected — the theme override is dark-only).
    color: (theme: Theme) =>
        theme.palette.mode === 'dark'
            ? theme.palette.getContrastText(theme.palette.info.light)
            : undefined,
    '& .MuiAlert-action': {
        py: 1,
        alignItems: 'center',
    },
    '& .MuiAlert-icon': {
        alignItems: 'center',
    },
    '& .MuiAlert-message': {
        display: 'flex',
        alignItems: 'center',
        flex: 1,
    },
};

const styleTitle = {
    color: 'inherit',
    display: 'block',
    textAlign: 'left',
};

const styleIcon = { width: '22px', height: '22px', color: 'inherit' };

const styleCta = {
    flexShrink: 0,
    color: 'inherit',
    borderColor: 'inherit',
    '&:hover': {
        borderColor: 'inherit',
        backgroundColor: 'mask.black.4p',
    },
};

const styleButton = { height: 'initial' };

const AccessCodeHeaderShowing = () => {
    const { showRegistrationDialog } = useRegistrationDialog();
    const { formatMessage } = useIntl();
    const isMobile = useIsMobile();

    const linkProps = {
        onClick: () => {
            showRegistrationDialog({
                currentStep: RegistrationFlowStep.AccessCode,
                registrationFlow: RegistrationFlow.ExistingRegistration,
            }).catch((err) =>
                logger.error(
                    'Failed to show registration dialog',
                    {},
                    err as Error
                )
            );
        },
    };

    const action = !isMobile && (
        <Button
            sx={styleCta}
            trackButton="UnsponsoredBanner"
            endIcon={<ArrowForward />}
            variant="outlined"
            {...linkProps}
        >
            {formatMessage(commonMessages['common.access_code_banner.cta'])}
        </Button>
    );

    return (
        <>
            <ClickableWrapper
                isEntirelyClickable={isMobile}
                component={Button}
                trackButton="UnsponsoredBanner"
                sx={styleButton}
                {...linkProps}
            >
                <Alert
                    severity="info"
                    variant="filled"
                    icon={<UnlockOutline sx={styleIcon} />}
                    action={action}
                    data-testid="ACCESS-CODE-HEADER"
                    sx={styleRoot}
                >
                    <Typography variant="body1Demi" sx={styleTitle}>
                        {formatMessage(
                            commonMessages['common.access_code_banner.title']
                        )}
                    </Typography>
                </Alert>
            </ClickableWrapper>
        </>
    );
};

export function AccessCodeHeader() {
    const { userBlocker } = useUserBlocker();

    if (userBlocker === UserBlocker.Unsponsored) {
        return <AccessCodeHeaderShowing />;
    }
}
